import { useEffect, useMemo, useState } from "react"
import { faExternalLinkAlt, faGear, faImage, faPeopleGroup } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import List, { type ListItem } from "../../../components/List/List"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useRestaurantReservations } from "../../../hooks/RestaurantReservation/useRestaurantReservations"
import type {
  RestaurantReservationArea,
  RestaurantReservationSettings,
} from "../../../Objects/RestaurantReservation/RestaurantReservationTypes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { PATH_MAP } from "../../../configuration/PathMap"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { ReservationAreaPopup } from "./ReservationAreaPopup"
import { ReservationSettingsPopup } from "./ReservationSettingsPopup"

export const ReservationAreasPage: React.FC = () => {
  const { t } = useTranslation(["restaurantReservations", "list"])

  const api = useRestaurantReservations()

  const { setNewConfirmation } = useConfirmation()

  const [searchParams] = useSearchParams()

  const [areas, setAreas] = useState<RestaurantReservationArea[]>([])

  const [settings, setSettings] = useState<RestaurantReservationSettings | null>(null)

  const [editingArea, setEditingArea] = useState<RestaurantReservationArea | undefined>()

  const [showArea, setShowArea] = useState(false)

  const [showSettings, setShowSettings] = useState(false)

  const load = async () => {
    const data = await api.getManagement()

    if (!data) return

    setAreas(data.areas)

    setSettings(data.settings)
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void load().then(() => {
        const action = searchParams.get("quickAction")
        if (action === "reservation-area-create") setShowArea(true)
        if (action === "reservation-settings") setShowSettings(true)
      })
    }, 0)

    return () => window.clearTimeout(timer)
  }, [])

  const cards = useMemo<ListItem[]>(
    () =>
      areas.map((area, index) => ({
        id: area.id,
        index: area.displayOrder || index,
        title: area.name,
        linkImage: area.iconUrl,
        isDisable: !area.isActive,
        dataFilter: [area.name, String(area.capacity)],
        children: (
          <div
            className={`group flex flex-col gap-4 rounded-2xl border p-4 transition-all duration-300 ${
              area.isActive
                ? "border-orange-100 bg-gradient-to-r from-orange-50 to-white hover:scale-[1.01] hover:shadow-lg"
                : "border-slate-200 bg-slate-100 opacity-75"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 flex-1 gap-3">
                {!area.iconUrl && (
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-200 text-orange-700">
                    <FontAwesomeIcon icon={faImage} />
                  </div>
                )}

                <div className="min-w-0">
                  <p className="truncate text-base font-extrabold tracking-tight text-slate-900">{area.name}</p>

                  <p className="mt-1 text-sm font-semibold text-slate-600">
                    {t("restaurantReservations:management.area_card_description")}
                  </p>
                </div>
              </div>

              <span
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${
                  area.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                }`}
              >
                {area.isActive
                  ? t("restaurantReservations:common.active")
                  : t("restaurantReservations:common.inactive")}
              </span>
            </div>

            <div className="border-t border-orange-100 pt-3">
              <span className="flex w-fit items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-sm font-bold text-orange-700">
                <FontAwesomeIcon icon={faPeopleGroup} />
                {t("restaurantReservations:management.capacity_short", { count: area.capacity })}
              </span>
            </div>
          </div>
        ),
      })),
    [areas, t],
  )

  const openArea = (area?: RestaurantReservationArea) => {
    setEditingArea(area)
    setShowArea(true)
  }

  const handleCopyArea = () => {
    if (!editingArea) {
      return
    }

    setEditingArea({ ...editingArea, id: 0, iconUrl: "" })
  }

  const requestAreaRemoval = (area: RestaurantReservationArea) =>
    new Promise<boolean>((resolve) => {
      setNewConfirmation(t("actions.remove_area"), t("management.remove_area_confirmation", { name: area.name }), async () => {
        const removed = await api.deleteArea(area.id)
        if (removed) {
          setEditingArea(undefined)
          await load()
        }

        resolve(!!removed)
      })
    })

  return (
    <>
      <div className={defaultStyle.containerList}>
        <List
          enableLocalPagination
          messageCount={t("restaurantReservations:management.areas_count")}
          messageEmpty={t("restaurantReservations:management.areas_empty")}
          nameFindSearch={t("restaurantReservations:management.areas_search")}
          data={cards}
          nameDropdown={Object.mapEnumToStrings(EEnableStatusType)}
          handleAddButton={() => openArea()}
          handleRefreshButton={load}
          handleExtraFloatButton={() => setShowSettings(true)}
          extraFloatButtonIcon={faGear}
          extraFloatButtonDescriptionKey="interactionDescriptions:restaurantReservations.areas.settings"
          handleSecondaryExtraFloatButton={
            companyMemory.slug
              ? () =>
                  window.open(
                    `${API_ROUTES.SITE.replace(/\/$/, "")}${PATH_MAP.PUBLIC_RESERVATIONS_LINK(companyMemory.slug)}`,
                    "_blank",
                    "noopener,noreferrer",
                  )
              : undefined
          }
          secondaryExtraFloatButtonIcon={faExternalLinkAlt}
          secondaryExtraFloatButtonDescriptionKey="interactionDescriptions:restaurantReservations.areas.openPublic"
          handleOnClick={(id) => openArea(areas.find((area) => area.id === id))}
        />
      </div>

      {showArea && (
        <ReservationAreaPopup
          area={editingArea}
          isLoading={api.isLoading}
          onClose={() => setShowArea(false)}
          onCopy={handleCopyArea}
          onRemove={requestAreaRemoval}
          onSave={async (value, icon) => {
            const saved = await api.saveArea(value, icon)
            if (!saved) return false
            await load()
            return true
          }}
        />
      )}

      {showSettings && settings && (
        <ReservationSettingsPopup
          settings={settings}
          isLoading={api.isLoading}
          onClose={() => setShowSettings(false)}
          onSave={async (value) => {
            const saved = await api.saveSettings(value)
            if (!saved) return false
            setSettings(saved)
            return true
          }}
        />
      )}
    </>
  )
}
